import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Text, Stack, Loader, Group, Button, Alert } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useSalon } from '../../context/SalonContext';
import { useApp } from '../../context/AppContext';
import EmailReceiptForm from './EmailReceiptForm';

function PaymentProcessingModal({ opened, onClose, apptId, paymentIntentId, customerEmail, priceAdjustmentReason, appointment }) {
  const { businessInfo } = useSalon();
  const { processPayment, pollStripePaymentStatus, simulatePayment, cancelPaymentIntent, updateAppointment } = useApp();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('requires_payment_method');
  const [isPolling, setIsPolling] = useState(true);
  const isPollingRef = useRef(true); // Needed for live references to value for the polling interval
  const pollIntervalRef = useRef(null);
  const pollCountRef = useRef(0);
  const maxPolls = 12;
  const [showNavigationModal, setShowNavigationModal] = useState(false);

  const clearPollInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const updatePollingStatus = (status) => {
    isPollingRef.current = status;
    setIsPolling(status);
  };

  const startPolling = useCallback(async () => {
    if (!isPollingRef.current) {
      clearPollInterval();
      return;
    }

    try {
      const response = await pollStripePaymentStatus(apptId);

      if (response) {
        setPaymentStatus(response.status);
        if (response.status === 'succeeded' || response.status === 'canceled' || response.status === null) {
          updatePollingStatus(false);
          clearPollInterval();
          return;
        }
      }

      pollCountRef.current++;
      if (pollCountRef.current >= maxPolls) {
        updatePollingStatus(false);
        clearPollInterval();
      }
    } catch (err) {
      setError(err.message);
      updatePollingStatus(false);
      clearPollInterval();
    }
  }, [apptId, clearPollInterval]);

  const initiatePayment = useCallback(async () => {
    try {
      const response = await processPayment({
        terminalDeviceId: businessInfo.stripe_terminal_device_id,
        paymentIntentId: paymentIntentId,
        accountId: businessInfo.stripe_connect_account_id
      });

      if (response.error) {
        setError(response.error);
        isPollingRef.current = false;
        return false;
      }
      return true;
    } catch (err) {
      setError(err.message);
      isPollingRef.current = false;
      return false;
    }
  }, [businessInfo, paymentIntentId]);

  const startProcess = useCallback(async () => {
    pollCountRef.current = 0;
    const success = await initiatePayment();
    if (success && isPollingRef.current) {
      pollIntervalRef.current = setInterval(startPolling, 5000);
    }
  }, [initiatePayment, startPolling]);

  useEffect(() => {
    if (opened) {
      updatePollingStatus(true);
      pollCountRef.current = 0;
      setPaymentStatus('requires_payment_method');
    } else {
      updatePollingStatus(false);
      clearPollInterval();
    }
  }, [opened]);

  useEffect(() => {
    if (!paymentIntentId) return;
    startProcess();
    return clearPollInterval;
  }, [paymentIntentId, startProcess, clearPollInterval]);

  const renderStatusIcon = () => {
    switch (paymentStatus) {
      case 'succeeded':
        return <IconCheck size={60} color="limegreen" style={{ padding: '12px',borderRadius: '100%', border: '2px solid limegreen' }} />;
      case 'canceled':
      case null:
        return <IconX size={60} color="red" style={{ padding: '12px', borderRadius: '100%', border: '2px solid red', marginBottom: '16px' }} />;
      default:
        return <Loader size="xl" color="violet" />;
    }
  };

  const handleSimulation = async (cardNumber) => {
    try {
      const response = await simulatePayment({
        terminalDeviceId: businessInfo.stripe_terminal_device_id,
        cardNumber: cardNumber,
        accountId: businessInfo.stripe_connect_account_id
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClose = async () => {
    isPollingRef.current = false;
    if (paymentStatus !== 'succeeded' && paymentStatus !== null && paymentStatus !== 'canceled') {
      try {
        await cancelPaymentIntent({
          paymentIntentId: paymentIntentId,
          accountId: businessInfo.stripe_connect_account_id
        });
      } catch (err) {
        console.error('Error canceling payment intent:', err);
      }
    }
    if (paymentStatus === 'succeeded') {
      const priceAdjustmentNote = priceAdjustmentReason ? `Price Adjustment Reason: ${priceAdjustmentReason}` : '';
      setShowNavigationModal(true);
      updateAppointment({
        id: apptId,
        salon_notes: appointment?.salon_notes ? appointment.salon_notes + ` | ${priceAdjustmentNote}` : priceAdjustmentNote
      });
      setTimeout(() => {
        navigate('/admin/calendar');
      }, 3000);
    } else {
      onClose();
    }
  };

  const renderHeader = () => {
    switch (paymentStatus) {
      case 'requires_payment_method':
      case 'processing':
      case 'requires_action':
      case 'requires_confirmation':
        return <Text size="lg" ta="center">Continue processing payment using the WisePOS E reader...</Text>;
      case 'succeeded':
        return <Text size="lg" ta="center">All set! Your payment has been succesfully processed.</Text>;
      case 'canceled':
      case null:
        return <Text size="lg" ta="center">Payment canceled.</Text>;
      default:
        return <Text size="lg" ta="center">Unexpected payment processing error.</Text>;
    }
  };

  const renderSubText = () => {
    switch (paymentStatus) {
      case 'requires_payment_method':
        return <Text c="dimmed" fs="italic">Waiting for payment method...</Text>;
      case 'processing':
        return <Text c="dimmed" fs="italic">Processing payment...</Text>;
      case 'requires_action':
      case 'requires_confirmation':
        return <Text c="dimmed" fs="italic">Waiting for customer action...</Text>;
      case 'succeeded':
      case 'canceled':
      case null:
        return null;
      default:
        return <Text c="dimmed" fs="italic">If this issue persists, please contact support at (617) 237-6592 or contact@getpagoda.com.</Text>;
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Processing Payment"
        size="lg"
        centered
      >
        <Stack gap="xl" p="sm">
          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          {renderHeader()}

          <Stack align="center" spacing="md">
            {renderStatusIcon()}
            {renderSubText()}
          </Stack>

          {paymentStatus === 'succeeded' && (
            <EmailReceiptForm 
              paymentIntentId={paymentIntentId}
              customerEmail={customerEmail}
            />
          )}

          {(paymentStatus === 'requires_payment_method' || 
            paymentStatus === 'processing' || 
            paymentStatus === 'requires_action' || 
            paymentStatus === 'requires_confirmation') && (
            <Text c="red" fw={700} ta="center">
              Please do not close or refresh your page.
            </Text>
          )}

          {!isPolling && !error && pollCountRef.current >= maxPolls && paymentStatus !== 'succeeded' && paymentStatus !== 'canceled' && paymentStatus !== null && (
            <Button 
              onClick={() => {
                updatePollingStatus(true);
                startProcess();
              }}
              fullWidth
            >
              Continue checking reader status
            </Button>
          )}

          {/* For testing purposes only, shouldn't show up in production */}
          {process.env.REACT_APP_SHOW_PAYMENT_SIMULATION === 'true' && paymentStatus !== 'succeeded' && paymentStatus !== 'canceled' && paymentStatus !== null && (
            <Group grow>
              <Button 
                onClick={() => handleSimulation('4242424242424242')}
                color="green"
                disabled={error || paymentStatus === 'succeeded' || paymentStatus === 'canceled' || paymentStatus === null}
              >
                Simulate Success
              </Button>
              <Button 
                onClick={() => cancelPaymentIntent({
                  paymentIntentId: paymentIntentId,
                  accountId: businessInfo.stripe_connect_account_id
                })}
                color="red"
                disabled={error || paymentStatus === 'succeeded' || paymentStatus === 'canceled' || paymentStatus === null}
              >
                Simulate Canceled
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={showNavigationModal}
        onClose={() => {}} // Empty function to prevent closing
        withCloseButton={false}
        size="sm"
        centered
      >
        <Stack align="center" spacing="md" py="md">
          <Loader size="md" color="violet" />
          <Text ta="center">
            Navigating back to appointments...
          </Text>
        </Stack>
      </Modal>
    </>
  );
}

export default PaymentProcessingModal; 